import React, {useEffect, useState, useContext, useRef, useMemo} from 'react';
import styled from 'styled-components';
import {labels} from '../../../../../../util/utils';
import {
	WIDTH_50,
	WIDTH_30,
	WIDTH_40,
	clientSideEventNames,
	formBodyType,
	riskTypes,
	entities
} from '../../../../../../util/uiconstants';
import Utility from '../../../../../../util/utilityfunctions';
import {
	useDocumentId,
	useEngagementId,
	useUserPreferences,
	useCanvasFormMetadata
} from '../../../../../../util/customHooks';
import EllipsesControl from '../../../../EllipsesControl/EllipsesControl';
import BodyTree from './BodyTree';
import CompareSnapshotLabel from '../../../../CanvasForm/CompareSnapshotLabel';
import {FormBodyTypeIdContext} from '../../FormBodyContainer';
import EditADCContainer from '../../../../../Main/ADC/EditADCContainer';
import {DocumentEngagementIdContext, DocumentEntityIdContext} from '../../../../../Main/Forms/CanvasFormContainer';
import {RelatedCanvasForm} from '../../../Section/RelatedCanvasForm';
import {isColumnVisibleForEntity} from './config';
import CreateEditControlModalWrapper from '../../../../EntityCrud/CreateEditControlModalWrapper';

export default function ControlRelatedObject(props) {
	const {data, isTopLevel, bodyId, isSnapshotView} = props,
		currentLevel = isTopLevel ? 1 : data.level;

	const [controlData, setControlData] = useState(data),
		[nameWidth, setNameWidthDockView] = useState((window.innerWidth * WIDTH_40) / 100),
		[showModal, setShowModal] = useState(false),
		[isObjectAssociatedToOtherForm, setIsObjectAssociatedToOtherForm] = useState(false);

	const {isShared, isTargetEngagement} = useCanvasFormMetadata();

	const formBodyTypeId = useContext(FormBodyTypeIdContext),
		engagementId = useEngagementId(),
		documentEngagementId = useContext(DocumentEngagementIdContext),
		documentId = useDocumentId(),
		documentEntityId = useContext(DocumentEntityIdContext);

	const designEffectiveness =
			controlData.designEffectiveness === undefined ||
			controlData.designEffectiveness === null ||
			controlData.designEffectiveness === ''
				? 0
				: controlData.designEffectiveness,
		designEffectivenessLabel = labels.controlDesignEffectiveness[designEffectiveness].description;

	const controlTypeId =
		controlData.controlTypeId === undefined || controlData.controlTypeId === null ? 0 : controlData.controlTypeId;
	const controlTypeLabel = controlTypeId === 'none' ? controlTypeId : labels.controlType[controlTypeId].controlTypeName;

	const userPreferences = useUserPreferences();

	let designEffectivenessStyle;

	if (designEffectiveness !== undefined) {
		designEffectivenessStyle = 'effectiveness-' + (designEffectiveness === 2 ? 'ineffective' : 'not-selected');
	}

	const modalRef = useRef();

	//This is until all entities are migrated to CreateUpdateEntityContainer
	const createUpdateEntities = useMemo(() => [entities.Control], [entities]);

	const loadEdit = () => {
		if (!isSnapshotView) {
			setShowModal(!showModal);

			if (modalRef.current && modalRef.current.toggleModal) {
				modalRef.current.toggleModal();
			}
		}
	};

	const showHideModel = (showModal) => {
		setShowModal(showModal);
	};

	const showHideIcon = () => {
		if (
			(controlData && controlData.secondaryRelatedCanvasForm && controlData.secondaryRelatedCanvasForm.length > 0) ||
			(controlData && controlData.primaryRelatedCanvasForm && controlData.primaryRelatedCanvasForm.length > 0)
		) {
			setIsObjectAssociatedToOtherForm(true);
		}
	};

	const onCollapseChange = (e) => {
		if (e.detail.isExpanded) {
			let width = (window.innerWidth * WIDTH_30) / 100;
			setNameWidthDockView(width);
		} else {
			let width = (window.innerWidth * WIDTH_50) / 100;
			setNameWidthDockView(width);
		}
	};

	useEffect(() => {
		showHideIcon();

		window.addEventListener(clientSideEventNames.onCollapseChange, onCollapseChange);
		return () => {
			window.removeEventListener(clientSideEventNames.onCollapseChange, onCollapseChange);
		};
	}, []);

	useEffect(() => {
		let value = {...data};
		if (data && data.children && data.children.length > 0) {
			let filteredControls = data.children.filter((risk) => {
				if (risk.entityId === entities.Risk) {
					return risk.riskTypeId !== riskTypes.INHERENTRISK;
				}
			});

			if (formBodyTypeId === formBodyType.RelatedRisks) {
				value = {...value, children: filteredControls};
			}
		}
		setControlData(value);
	}, [data]);

	let controlContent;

	// Columns visibility:
	const showControlType = isColumnVisibleForEntity(
		controlData.entityId,
		'type',
		bodyId,
		formBodyTypeId,
		documentEntityId,
		isSnapshotView
	);
	const showDesignEffectiveness = isColumnVisibleForEntity(
		controlData.entityId,
		'designEffectiveness',
		bodyId,
		formBodyTypeId,
		documentEntityId,
		isSnapshotView
	);
	const showRelatedCanvasForms = isColumnVisibleForEntity(
		controlData.entityId,
		'relatedForms',
		bodyId,
		formBodyTypeId,
		documentEntityId,
		isSnapshotView
	);

	switch (formBodyTypeId) {
		// eslint-disable-next-line no-case-declarations
		case formBodyType.RelatedRisks: {
			let testingLabel;
			switch (controlData.hasControlTesting) {
				case true:
					testingLabel = labels.yes;
					break;
				case false:
					testingLabel = labels.no;
					break;
				case null:
					testingLabel = labels.notSelected;
					break;
			}
			controlContent = (
				<>
					<section
						className={`entity-content-main RelatedRisksCol ${Utility.getClasstoDisableADCLink(
							userPreferences,
							isSnapshotView
						)}`}
					>
						<EllipsesControl
							onClick={loadEdit}
							content={Utility.getHtmlTextElement(controlData.entityName, isSnapshotView)}
							tooltip={Utility.getHtmlTextElement(controlData.entityName, isSnapshotView)}
							noOfLines={1}
						/>
					</section>
					<section
						className={`entity-content-data ${Utility.getClasstoDisableADCLink(userPreferences, isSnapshotView)}`}
					>
						{controlData && <EllipsesControl content={testingLabel} tooltip={testingLabel} noOfLines={1} />}
						{isSnapshotView && <CompareSnapshotLabel snapshotObject={controlData} />}
					</section>
				</>
			);
			break;
		}
		default:
			controlContent = (
				<>
					{!isSnapshotView ? (
						<>
							<section
								className={`entity-content-main controlEntityContentMain ${
									!bodyId ? '' : 'controlEntityContentWrapper'
								} ${Utility.getClasstoDisableADCLink(userPreferences, isSnapshotView)}`}
							>
								<section className="RelatedObjectTextWrapper">
									<EllipsesControl
										onClick={loadEdit}
										content={controlData.entityName}
										tooltip={controlData.entityName}
										noOfLines={3}
									/>
								</section>
							</section>
							{showControlType && (
								<section className="entity-content-data SecondCol">
									{controlData && controlData.controlTypeId && (
										<EllipsesControl content={controlTypeLabel} tooltip={controlTypeLabel} noOfLines={1} />
									)}
								</section>
							)}
							{showDesignEffectiveness && (
								<section
									className={
										'entity-content-data ThirdCol' + (designEffectivenessStyle && ` ${designEffectivenessStyle}`)
									}
								>
									<EllipsesControl
										content={designEffectivenessLabel}
										tooltip={designEffectivenessLabel}
										noOfLines={1}
									/>
								</section>
							)}
							{showRelatedCanvasForms && (
								<section className={'entity-content-data FourthCol WcgwIcon'}>
									{isObjectAssociatedToOtherForm && controlData && controlData.controlTypeId && (
										<section className="WcgwRelated">
											<RelatedCanvasForm
												relatedCanvasFormDetails={controlData}
												documentEngagementId={documentEngagementId}
											/>
										</section>
									)}
								</section>
							)}
						</>
					) : (
						<>
							{' '}
							<section
								className={`entity-content-main ${Utility.getClasstoDisableADCLink(userPreferences, isSnapshotView)}`}
							>
								<EllipsesControl
									content={Utility.getHtmlTextElement(controlData.entityName, isSnapshotView)}
									tooltip={Utility.getHtmlTextElement(controlData.entityName, isSnapshotView)}
									noOfLines={1}
									width={nameWidth}
								/>
							</section>
							<CompareSnapshotLabel snapshotObject={controlData} />{' '}
						</>
					)}
				</>
			);
			break;
	}

	let customData;

	if (isTopLevel && formBodyTypeId === formBodyType.RelatedWCGWsSCOTs) {
		customData = {
			entityName: 'Placeholder item',
			children: [
				{
					entityName: labels.formsScotsAndWcgws,
					children:
						controlData.children && controlData.children.length > 0
							? [...controlData.children]
							: [{entityName: labels.formsNoRelatedObjects}]
				}
			]
		};
	}

	let noChildrenLabel;

	switch (formBodyTypeId) {
		case formBodyType.RelatedRisks:
			noChildrenLabel = labels.noRelatedRisks;
			break;
	}

	let containerJsx = null;

	if (!isSnapshotView) {
		if (createUpdateEntities.includes(data.entityId)) {
			if (data.entityId === entities.Control) {
				containerJsx = showModal && (
					<CreateEditControlModalWrapper
						bodyId={bodyId}
						isEdit
						documentId={documentId}
						showModal={showModal}
						setShowModal={setShowModal}
						onClose={setShowModal}
						controlId={Number(data.entityUId)}
						viewOnly={isShared && isTargetEngagement ? true : false}
					/>
				);
			} else {
				containerJsx = showModal && (
					<CreateEditControlModalWrapper
						bodyId={bodyId}
						isEdit
						documentId={documentId}
						showModal={showModal}
						setShowModal={setShowModal}
						onClose={setShowModal}
						controlId={Number(data.entityUId)}
					/>
				);
			}
		}
	}

	return (
		<StyledControlRelatedObject
			className={`ControlRelatedObject entity-content entity-content-level-${currentLevel}
						${formBodyTypeId === formBodyType.RelatedRisks ? 'RelatedRisksContainer' : ''}`}
		>
			{containerJsx}
			<BodyTree
				key={bodyId}
				bodyId={bodyId}
				data={customData || data}
				isSnapshotView={isSnapshotView}
				defaultOpen
				open
				noChildrenLabel={noChildrenLabel}
			>
				{controlContent}
			</BodyTree>
		</StyledControlRelatedObject>
	);
}

const StyledControlRelatedObject = styled.section`
	&.entity-content {
		&.entity-content-level-1 {
			.tree-trigger-children {
				line-height: normal;
				width: calc(100% - var(--px-30));
			}
			.controlEntityContentMain {
				width: 100%;
				line-height: normal;
				padding-right: var(--px-10);
				.RelatedObjectTextWrapper {
					width: 100% !important;
					cursor: default !important;
					&:hover {
						text-decoration: none;
					}
					.ellipses-multilines {
						cursor: pointer !important;
						&:hover {
							text-decoration: underline;
						}
					}
				}
				&.disableLinksExternalUsers {
					pointer-events: none;
				}
			}
		}
		&.RelatedRisksContainer {
			.tree-trigger-children {
				width: calc(100% - var(--px-20));
				.RelatedRisksCol {
					width: calc(100% - var(--px-160)) !important;
					padding-right: var(--px-10);
					&.disableLinksExternalUsers {
						pointer-events: none;
					}
					.ellipses {
						div {
							cursor: pointer !important;
							&:hover {
								text-decoration: underline;
							}
						}
					}
				}
				.entity-content-data {
					max-width: var(--px-200);
					min-width: var(--px-150);
					margin-left: 0;
					padding-left: var(--px-3);
				}
			}
		}
		&.entity-content-level-2 {
			margin-left: var(--px-20);
			border-top: var(--px-1) solid var(--neutrals-200);
			padding: var(--px-3) 0 var(--px-3) var(--px-10) !important;
			.entity-content-main {
				margin-top: var(--px-5);
				&.controlEntityContentWrapper {
					width: 54.5% !important;
					padding-right: 0 !important;
					margin-left: var(--px-3);
					@media only screen and (max-width: 1280px) {
						margin-left: 0;
					}
					&.disableLinksExternalUsers {
						pointer-events: none;
					}
				}
				.RelatedObjectTextWrapper {
					width: 100% !important;
				}
			}
			.entity-content-data {
				&.SecondCol {
					padding: var(--px-2) var(--px-10) 0 0;
					color: var(--neutrals-700);
				}
				&.ThirdCol {
					padding-top: var(--px-2);
					color: var(--neutrals-700);
					padding-left: var(--px-8);
				}
			}
		}
		&.RelatedRisksContainer {
			.motif-tree-content-visible {
				display: block;
				.RelatedRisksCol {
					width: calc(100% - var(--px-200)) !important;
					display: inline-flex;
					&.disableLinksExternalUsers {
						pointer-events: none;
					}
				}
				.entity-content-data {
					width: var(--px-190);
					display: inline-flex;
					margin-left: 0;
				}
				svg {
					display: none !important;
				}
				.TestingCol {
					margin-left: var(--px-150);
				}
			}
			.noRelatedObjectsFound {
				padding-bottom: var(--px-10);
			}
		}
	}
`;
